/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Revoke Developer API Key Modal (Task 10.6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  ShieldAlert,
  Trash2,
  X,
  AlertTriangle,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useApiKeyStore } from '../../stores/useApiKeyStore';

export const RevokeKeyModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    isRevokeModalOpen,
    keyToRevoke,
    setRevokeModalOpen,
    revokeKey,
  } = useApiKeyStore();

  const [isRevoking, setIsRevoking] = useState(false);

  if (!isRevokeModalOpen || !keyToRevoke) return null;

  const handleConfirmRevoke = async () => {
    setIsRevoking(true);
    try {
      await revokeKey(keyToRevoke.id);
    } finally {
      setIsRevoking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <span className="p-2 rounded-xl bg-rose-500/10 text-rose-500 border border-rose-500/20">
              <ShieldAlert className="size-4" />
            </span>
            <h3 className="text-sm font-semibold text-text">
              {t('apikeys.revokeModalTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => setRevokeModalOpen(false)}
            className="p-1 rounded-lg hover:bg-surface-subtle text-text-muted hover:text-text transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 text-xs">
          <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/5 text-rose-600 dark:text-rose-400 flex items-start gap-2.5">
            <AlertTriangle className="size-4 shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              {t('apikeys.revokeModalDesc', { name: keyToRevoke.name })}
            </p>
          </div>

          <div className="p-3 rounded-xl border border-border bg-surface-subtle space-y-1.5 font-mono text-[11px]">
            <div className="flex items-center justify-between text-text-muted">
              <span>Key ID:</span>
              <span className="text-text font-bold">{keyToRevoke.id}</span>
            </div>
            <div className="flex items-center justify-between text-text-muted">
              <span>Key Prefix & Hint:</span>
              <span className="text-text font-bold">
                {keyToRevoke.keyPrefix}••••••••{keyToRevoke.keyHint}
              </span>
            </div>
            <div className="flex items-center justify-between text-text-muted">
              <span>Environment:</span>
              <span className="uppercase text-text font-bold">
                {keyToRevoke.keyPrefix.includes('test') ? 'TEST' : 'LIVE'}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-2 p-4 bg-surface-subtle border-t border-border">
          <button
            type="button"
            disabled={isRevoking}
            onClick={() => setRevokeModalOpen(false)}
            className="px-4 py-2 text-xs font-medium rounded-xl border border-border hover:bg-surface text-text transition-colors"
          >
            {t('apikeys.cancelBtn')}
          </button>
          <button
            type="button"
            disabled={isRevoking}
            onClick={handleConfirmRevoke}
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 transition-all flex items-center gap-1.5 shadow-sm"
          >
            <Trash2 className="size-4" />
            <span>{isRevoking ? 'Revoking...' : t('apikeys.revokeConfirmBtn')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
